import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AgoraRTM from 'agora-rtm';
import axios from 'axios';

const AgoraRTMContext = createContext(null);

// Retry helper with exponential backoff
async function retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000, description = 'operation') {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const isLastAttempt = attempt === maxRetries - 1;
      if (isLastAttempt) {
        console.error(`[RTM] ${description} failed after ${maxRetries} attempts:`, error.message || error);
        throw error;
      }
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`[RTM] ${description} failed (attempt ${attempt + 1}/${maxRetries}), retrying in ${delay}ms...`);
      await new Promise(r => setTimeout(r, delay));
    }
  }
}

// Helper to get RTM token from backend with retry
async function getAgoraRTMToken(userId) {
  return retryWithBackoff(async () => {
    const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/rtm-token`, {
      userId: String(userId),
    });
    if (!response.data.token) {
      throw new Error('No token in response');
    }
    return response.data.token;
  }, 3, 1000, 'RTM token fetch');
}

export function AgoraRTMProvider({ children, meetingId, userId, userName }) {

  const [rtmClient, setRtmClient] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isChannelReady, setIsChannelReady] = useState(false);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState(new Map());
  const [typingUsers, setTypingUsers] = useState(new Set());
  // Phase 2: Presence snapshot - live-updated map of userId → { states, ... }
  const [presenceSnapshot, setPresenceSnapshot] = useState(new Map());

  const clientRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  // Command listeners registry (PER-PROVIDER instance)
  const commandListenersRef = useRef(new Map());
  // Pending message/command queues for buffering during initialization
  const pendingMessagesRef = useRef([]);
  const pendingCommandsRef = useRef([]);
  // Reconnection state
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const isReconnectingRef = useRef(false);
  const mountedRef = useRef(true);
  const authFailedRef = useRef(false); // Track auth failures to prevent health check retries

  const appId = process.env.REACT_APP_AGORA_APP_ID;
  const channelNameStr = meetingId ? `chat_${meetingId}` : null;

  // Flush pending messages and commands when channel becomes ready
  const flushPendingQueues = useCallback(async () => {
    const client = clientRef.current;
    if (!client || !isChannelReady || !channelNameStr) return;

    // Flush pending messages
    while (pendingMessagesRef.current.length > 0) {
      const { text, attachments, resolve } = pendingMessagesRef.current.shift();
      try {
        const timestamp = Date.now();
        const messageData = JSON.stringify({
          type: 'message',
          text: text?.trim() || '',
          userName: userName || `User ${userId}`,
          timestamp: timestamp,
          image: attachments?.image || null,
          file: attachments?.file || null,
          replyTo: attachments?.replyTo || null,
        });
        await client.publish(channelNameStr, messageData);

        const consistentMessageId = `msg-${userId}-${timestamp}`;
        setMessages(prev => [...prev, {
          id: consistentMessageId,
          text: text?.trim() || '',
          userId: String(userId),
          userName: userName || `User ${userId}`,
          timestamp: timestamp,
          isLocal: true,
          image: attachments?.image || null,
          file: attachments?.file || null,
          replyTo: attachments?.replyTo || null,
        }]);
        resolve(true);
        console.log('[RTM] Flushed pending message');
      } catch (error) {
        console.error('[RTM] Failed to flush pending message:', error);
        resolve(false);
      }
    }

    // Flush pending commands
    while (pendingCommandsRef.current.length > 0) {
      const { topic, payload, resolve } = pendingCommandsRef.current.shift();
      try {
        const commandData = JSON.stringify({
          type: 'command',
          topic,
          payload,
          senderId: String(userId),
          senderName: userName || `User ${userId}`,
          timestamp: Date.now(),
        });
        await client.publish(channelNameStr, commandData);
        resolve(true);
        console.log(`[RTM] Flushed pending command: ${topic}`);
      } catch (error) {
        console.error(`[RTM] Failed to flush pending command ${topic}:`, error);
        resolve(false);
      }
    }
  }, [isChannelReady, userId, userName, channelNameStr]);

  // Flush queues when channel becomes ready
  useEffect(() => {
    if (isChannelReady) {
      flushPendingQueues();
    }
  }, [isChannelReady, flushPendingQueues]);

  // Initialize RTM client and login
  useEffect(() => {
    if (!meetingId || !userId || !appId) {
      console.log('[RTM] Missing required params - meetingId:', meetingId, 'userId:', userId, 'appId:', !!appId);
      setIsLoading(false);
      return;
    }

    mountedRef.current = true;
    let client = null;

    const handleMessageEvent = (event) => {
      // event: { channelType, channelName, topicName, publisher, message, publishTime, customType }
      const publisherId = event.publisher;
      const rawMessage = event.message;

      // Skip own messages (we handle them locally on publish)
      if (publisherId === String(userId)) return;

      try {
        const parsedMessage = JSON.parse(rawMessage);

        // Handle command messages (for PubSub/Mute All functionality)
        if (parsedMessage.type === 'command') {
          const { topic, payload, senderId, senderName, timestamp } = parsedMessage;

          console.log(`[RTM] Received command on topic "${topic}" from ${senderName} (${senderId}):`, payload);

          // Notify all listeners for this topic
          const listeners = commandListenersRef.current.get(topic);
          console.log(`[RTM] Found ${listeners?.size || 0} listeners for topic "${topic}"`);

          if (listeners && listeners.size > 0) {
            listeners.forEach(callback => {
              callback({
                message: payload,
                senderId: senderId || publisherId,
                senderName: senderName || 'Unknown',
                timestamp: timestamp || Date.now(),
                topic
              });
            });
          }
          return; // Don't process as chat message
        }

        if (parsedMessage.type === 'typing') {
          // Handle typing indicator
          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.add(parsedMessage.userName);
            return newSet;
          });
          // Clear typing after 3 seconds
          setTimeout(() => {
            setTypingUsers(prev => {
              const newSet = new Set(prev);
              newSet.delete(parsedMessage.userName);
              return newSet;
            });
          }, 3000);
        } else if (parsedMessage.type === 'message') {
          // CRITICAL: Use sender's timestamp for consistent message ID across all clients
          const senderTimestamp = parsedMessage.timestamp || Date.now();
          const consistentMessageId = `msg-${publisherId}-${senderTimestamp}`;

          setMessages(prev => [...prev, {
            id: consistentMessageId,
            text: parsedMessage.text,
            userId: publisherId,
            userName: parsedMessage.userName,
            timestamp: senderTimestamp,
            isLocal: false,
            image: parsedMessage.image || null,
            file: parsedMessage.file || null,
            replyTo: parsedMessage.replyTo || null,
          }]);
        }
      } catch (e) {
        // Plain text message
        const fallbackTimestamp = Date.now();
        const consistentMessageId = `msg-${publisherId}-${fallbackTimestamp}`;
        setMessages(prev => [...prev, {
          id: consistentMessageId,
          text: rawMessage,
          userId: publisherId,
          userName: `User ${publisherId}`,
          timestamp: fallbackTimestamp,
          isLocal: false,
        }]);
      }
    };

    const handlePresenceEvent = (event) => {
      // event: { eventType, channelType, channelName, publisher, stateChanged, snapshot, interval, timestamp }
      try {
      console.log(`[RTM] Presence event: ${event.eventType}`, event);

      switch (event.eventType) {
        case 'SNAPSHOT': {
          // Initial list of all online users + their states on join
          const snapshot = new Map();
          if (event.snapshot) {
            event.snapshot.forEach(user => {
              snapshot.set(user.userId, {
                id: user.userId,
                joinedAt: Date.now(),
                states: user.stateData || user.states || {},
              });
            });
          }
          setPresenceSnapshot(snapshot);
          // Also update participants map for backward compatibility
          setParticipants(prev => {
            const newMap = new Map(prev);
            snapshot.forEach((userData, uid) => {
              newMap.set(uid, { id: uid, joinedAt: userData.joinedAt });
            });
            return newMap;
          });
          break;
        }
        case 'REMOTE_JOIN': {
          const joinedUserId = event.publisher;
          console.log('[RTM] Member joined channel:', joinedUserId);
          setPresenceSnapshot(prev => {
            const newMap = new Map(prev);
            newMap.set(joinedUserId, {
              id: joinedUserId,
              joinedAt: Date.now(),
              states: {},
            });
            return newMap;
          });
          setParticipants(prev => {
            const newMap = new Map(prev);
            newMap.set(joinedUserId, { id: joinedUserId, joinedAt: Date.now() });
            return newMap;
          });
          break;
        }
        case 'REMOTE_LEAVE':
        case 'REMOTE_TIMEOUT': {
          const leftUserId = event.publisher;
          console.log('[RTM] Member left channel:', leftUserId);
          setPresenceSnapshot(prev => {
            const newMap = new Map(prev);
            newMap.delete(leftUserId);
            return newMap;
          });
          setParticipants(prev => {
            const newMap = new Map(prev);
            newMap.delete(leftUserId);
            return newMap;
          });
          break;
        }
        case 'REMOTE_STATE_CHANGED': {
          const changedUserId = event.publisher;
          const stateChanged = event.stateChanged || {};
          console.log('[RTM] Member state changed:', changedUserId, stateChanged);
          setPresenceSnapshot(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(changedUserId) || { id: changedUserId, joinedAt: Date.now(), states: {} };
            // Merge changed states into existing states
            const mergedStates = { ...existing.states };
            if (Array.isArray(stateChanged)) {
              stateChanged.forEach(item => {
                mergedStates[item.key] = item.value;
              });
            } else if (typeof stateChanged === 'object') {
              Object.assign(mergedStates, stateChanged);
            }
            newMap.set(changedUserId, { ...existing, states: mergedStates });
            return newMap;
          });
          break;
        }
        case 'INTERVAL': {
          // Aggregated presence events when channel has many users
          if (event.interval) {
            const { joinUserList, leaveUserList, timeoutUserList, userStateList } = event.interval;
            setPresenceSnapshot(prev => {
              const newMap = new Map(prev);
              // Handle joins
              joinUserList?.forEach(uid => {
                if (!newMap.has(uid)) {
                  newMap.set(uid, { id: uid, joinedAt: Date.now(), states: {} });
                }
              });
              // Handle leaves
              leaveUserList?.forEach(uid => newMap.delete(uid));
              timeoutUserList?.forEach(uid => newMap.delete(uid));
              // Handle state changes
              userStateList?.forEach(item => {
                const existing = newMap.get(item.userId) || { id: item.userId, joinedAt: Date.now(), states: {} };
                const mergedStates = { ...existing.states };
                if (item.states) {
                  Object.assign(mergedStates, item.states);
                }
                newMap.set(item.userId, { ...existing, states: mergedStates });
              });
              return newMap;
            });
            // Also update participants
            setParticipants(prev => {
              const newMap = new Map(prev);
              event.interval.joinUserList?.forEach(uid => {
                newMap.set(uid, { id: uid, joinedAt: Date.now() });
              });
              event.interval.leaveUserList?.forEach(uid => newMap.delete(uid));
              event.interval.timeoutUserList?.forEach(uid => newMap.delete(uid));
              return newMap;
            });
          }
          break;
        }
        default:
          break;
      }
      } catch (err) {
        console.error('[RTM] Error handling presence event:', err);
      }
    };

    const handleStatusEvent = (event) => {
      // event: { state, reason }
      console.log(`[RTM] Connection state changed: ${event.state}, reason: ${event.reason}`);
      if (event.state === 'CONNECTED') {
        setIsLoggedIn(true);
        reconnectAttemptsRef.current = 0;
        authFailedRef.current = false; // Clear auth failure on successful connection
      } else if (event.state === 'DISCONNECTED' || event.state === 'FAILED') {
        setIsLoggedIn(false);
        setIsChannelReady(false);
        // Only auto-reconnect for network disconnections, NOT for auth failures
        // REJECTED_BY_SERVER means invalid token — retrying with the same token is futile
        // LOGOUT means intentional disconnect — don't reconnect
        const noReconnectReasons = ['LOGOUT', 'REJECTED_BY_SERVER'];
        if (noReconnectReasons.includes(event.reason)) {
          authFailedRef.current = true; // Prevent health check from retrying
        }
        if (mountedRef.current && !noReconnectReasons.includes(event.reason)) {
          attemptReconnect();
        }
      }
    };

    const attemptReconnect = async () => {
      if (isReconnectingRef.current || !mountedRef.current) return;
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.error(`[RTM] Max reconnection attempts (${maxReconnectAttempts}) reached`);
        return;
      }

      isReconnectingRef.current = true;
      reconnectAttemptsRef.current++;
      const delay = 1000 * Math.pow(2, reconnectAttemptsRef.current - 1);
      console.log(`[RTM] Attempting reconnection ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${delay}ms...`);

      await new Promise(r => setTimeout(r, delay));

      if (!mountedRef.current) {
        isReconnectingRef.current = false;
        return;
      }

      try {
        // Get fresh token and create new client
        const token = await getAgoraRTMToken(userId);
        const channelName = `chat_${meetingId}`;

        // Remove listeners from the previous client before replacing it
        // — without this, every reconnect doubles the live handler count.
        const oldClient = clientRef.current;
        if (oldClient) {
          try {
            oldClient.removeEventListener('message', handleMessageEvent);
            oldClient.removeEventListener('presence', handlePresenceEvent);
            oldClient.removeEventListener('status', handleStatusEvent);
            oldClient.removeEventListener('TokenPrivilegeWillExpire', handleTokenExpiry);
          } catch (e) { /* ignore */ }
        }

        // For v2, we need to create a new client with the new token
        const newClient = new AgoraRTM.RTM(appId, String(userId), {
          useStringUserId: true,
          presenceTimeout: 300,
        });

        // Re-register event listeners
        newClient.addEventListener('message', handleMessageEvent);
        newClient.addEventListener('presence', handlePresenceEvent);
        newClient.addEventListener('status', handleStatusEvent);
        newClient.addEventListener('TokenPrivilegeWillExpire', handleTokenExpiry);

        await newClient.login({ token });
        await newClient.subscribe(channelName, {
          withPresence: true,
          withMessage: true,
          withMetadata: true,
          withLock: false,
        });

        clientRef.current = newClient;
        client = newClient;

        console.log('[RTM] Reconnection successful!');
        reconnectAttemptsRef.current = 0;
        if (mountedRef.current) {
          setRtmClient(newClient);
          setIsLoggedIn(true);
          setIsChannelReady(true);
        }
      } catch (error) {
        console.error('[RTM] Reconnection failed:', error.message || error);
        if (mountedRef.current) {
          setIsLoggedIn(false);
          setIsChannelReady(false);
        }
        isReconnectingRef.current = false;
        attemptReconnect();
        return;
      } finally {
        isReconnectingRef.current = false;
      }
    };

    const handleTokenExpiry = async () => {
      console.log('[RTM] Token about to expire, renewing...');
      try {
        const newToken = await getAgoraRTMToken(userId);
        if (clientRef.current) {
          await clientRef.current.renewToken(newToken);
          console.log('[RTM] Token renewed successfully');
        }
      } catch (error) {
        console.error('[RTM] Failed to renew token:', error);
      }
    };

    const initRTM = async () => {
      try {
        console.log('[RTM] Connecting... meetingId:', meetingId, 'userId:', userId, 'appId:', appId);

        // Get RTM token with retry
        console.log('[RTM] Fetching token...');
        const token = await getAgoraRTMToken(userId);
        console.log('[RTM] Token received');

        if (!mountedRef.current) return;

        // Create Signaling v2 client (token goes in login(), NOT constructor)
        const rtmConfig = {
          useStringUserId: true,
          presenceTimeout: 300,
          logUpload: false,
        };
        client = new AgoraRTM.RTM(appId, String(userId), rtmConfig);
        clientRef.current = client;
        console.log('[RTM] Signaling v2 client created successfully');

        // Set up event listeners (global in v2, not channel-specific)
        client.addEventListener('message', handleMessageEvent);
        client.addEventListener('presence', handlePresenceEvent);
        client.addEventListener('status', handleStatusEvent);
        client.addEventListener('TokenPrivilegeWillExpire', handleTokenExpiry);

        // Login to Signaling v2 with token and retry
        await retryWithBackoff(async () => {
          await client.login({ token });
        }, 3, 1000, 'RTM login');
        console.log('[RTM] Logged in successfully');

        if (!mountedRef.current) return;

        // Subscribe to channel with presence (replaces createChannel + join)
        const channelName = `chat_${meetingId}`;
        console.log('[RTM] Subscribing to channel:', channelName);
        await retryWithBackoff(async () => {
          await client.subscribe(channelName, {
            withPresence: true,
            withMessage: true,
            withMetadata: true,
            withLock: false,
          });
        }, 3, 1000, 'Channel subscribe');
        console.log('[RTM] Channel subscribed successfully with presence!');

        if (mountedRef.current) {
          setRtmClient(client);
          setIsLoggedIn(true);
          setIsChannelReady(true);
          setIsLoading(false);
          console.log('[RTM] Ready! Channel:', channelName, '(Signaling v2 with Presence)');
        }

      } catch (error) {
        console.error('[RTM] Failed:', error.message || error);
        if (mountedRef.current) {
          setIsLoading(false);
          attemptReconnect();
        }
      }
    };

    initRTM();

    // Health check: periodically verify RTM is connected and trigger reconnect if not
    const healthCheckInterval = setInterval(() => {
      if (!mountedRef.current) return;
      // Skip health check if auth failed (e.g., REJECTED_BY_SERVER) — retrying is futile
      if (authFailedRef.current) return;
      // If we should be connected but aren't, and not already reconnecting, trigger reconnect
      if (!isReconnectingRef.current && clientRef.current) {
        const clientState = clientRef.current?.connectionState || clientRef.current?._connectionState;
        if (clientState && clientState !== 'CONNECTED') {
          console.log(`[RTM] Health check: connection state is "${clientState}", triggering reconnect`);
          reconnectAttemptsRef.current = 0;
          attemptReconnect();
        }
      }
    }, 15000); // Check every 15 seconds

    return () => {
      mountedRef.current = false;
      clearInterval(healthCheckInterval);

      const cleanup = async () => {
        try {
          commandListenersRef.current.clear();
          pendingMessagesRef.current = [];
          pendingCommandsRef.current = [];
          // Remove listeners from whatever client is current — may differ
          // from `client` above if a reconnect swapped clientRef.current.
          const currentClient = clientRef.current || client;
          if (currentClient) {
            try {
              currentClient.removeEventListener('message', handleMessageEvent);
              currentClient.removeEventListener('presence', handlePresenceEvent);
              currentClient.removeEventListener('status', handleStatusEvent);
              currentClient.removeEventListener('TokenPrivilegeWillExpire', handleTokenExpiry);
            } catch (e) { /* ignore */ }
          }
          if (client && channelNameStr) {
            try {
              await client.unsubscribe(channelNameStr);
            } catch (e) {
              // Ignore unsubscribe errors
            }
            try {
              await client.logout();
            } catch (e) {
              // Ignore logout errors
            }
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      };

      cleanup();
    };
  }, [meetingId, userId, appId]);

  // Send message to channel with optional attachment data
  const sendMessage = useCallback(async (text, attachments = null) => {
    if (!text?.trim() && !attachments) return false;

    // If channel not ready, queue the message
    if (!clientRef.current || !isChannelReady || !channelNameStr) {
      console.log('[RTM] Channel not ready, queueing message...');
      return new Promise((resolve) => {
        pendingMessagesRef.current.push({ text, attachments, resolve });
      });
    }

    try {
      const timestamp = Date.now();
      const messageData = JSON.stringify({
        type: 'message',
        text: text?.trim() || '',
        userName: userName || `User ${userId}`,
        timestamp: timestamp,
        image: attachments?.image || null,
        file: attachments?.file || null,
        replyTo: attachments?.replyTo || null,
      });

      await clientRef.current.publish(channelNameStr, messageData);

      // CRITICAL: Use the SAME consistent ID format as receivers
      const consistentMessageId = `msg-${userId}-${timestamp}`;

      // Add to local messages
      setMessages(prev => [...prev, {
        id: consistentMessageId,
        text: text?.trim() || '',
        userId: String(userId),
        userName: userName || `User ${userId}`,
        timestamp: timestamp,
        isLocal: true,
        image: attachments?.image || null,
        file: attachments?.file || null,
        replyTo: attachments?.replyTo || null,
      }]);

      return true;
    } catch (error) {
      console.error('[AgoraRTM] Failed to send message:', error);
      return false;
    }
  }, [userId, userName, isChannelReady, channelNameStr]);

  // Send command to channel (for PubSub compatibility - mute all, etc.)
  const sendCommand = useCallback(async (topic, payload) => {
    console.log(`[RTM] sendCommand called for topic "${topic}", isLoggedIn: ${isLoggedIn}, isChannelReady: ${isChannelReady}, hasClient: ${!!clientRef.current}`);

    // If channel not ready, queue the command for later delivery
    if (!clientRef.current || !isChannelReady || !isLoggedIn || !channelNameStr) {
      console.log(`[RTM] Channel not ready, queueing command: ${topic} (pending queue size: ${pendingCommandsRef.current.length})`);
      return new Promise((resolve) => {
        pendingCommandsRef.current.push({ topic, payload, resolve });
      });
    }

    try {
      const commandData = JSON.stringify({
        type: 'command',
        topic,
        payload,
        senderId: String(userId),
        senderName: userName || `User ${userId}`,
        timestamp: Date.now(),
      });

      console.log(`[RTM] Sending command on topic "${topic}":`, payload);
      await clientRef.current.publish(channelNameStr, commandData);
      console.log(`[RTM] Command sent successfully on topic "${topic}"`);
      return true;
    } catch (error) {
      // If RTM disconnected mid-send, queue for retry instead of failing
      if (error?.message?.includes('not connected') || error?.code === -10025) {
        console.warn(`[RTM] RTM disconnected, queueing command for retry: ${topic}`);
        return new Promise((resolve) => {
          pendingCommandsRef.current.push({ topic, payload, resolve });
        });
      }
      console.error(`[RTM] Failed to send command on topic "${topic}":`, error);
      return false;
    }
  }, [userId, userName, isLoggedIn, isChannelReady, channelNameStr]);

  // Send typing indicator
  const sendTypingIndicator = useCallback(async () => {
    if (!clientRef.current || !isChannelReady || !channelNameStr) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
      const typingData = JSON.stringify({
        type: 'typing',
        userName: userName || `User ${userId}`,
      });

      await clientRef.current.publish(channelNameStr, typingData);
    } catch (error) {
      // Ignore typing indicator errors
    }

    // Set timeout to prevent spam
    typingTimeoutRef.current = setTimeout(() => {
      typingTimeoutRef.current = null;
    }, 2000);
  }, [userId, userName, isChannelReady, channelNameStr]);

  // Send private message (v2 uses inbox pattern: publish to user-specific channel)
  const sendPrivateMessage = useCallback(async (peerId, text) => {
    if (!clientRef.current || !text.trim() || !isLoggedIn) return false;

    try {
      const messageData = JSON.stringify({
        type: 'private',
        text: text.trim(),
        userName: userName || `User ${userId}`,
        timestamp: Date.now(),
      });

      // In Signaling v2, peer-to-peer is done via publishing to a user-specific channel
      // or by using the inbox pattern. For backward compatibility, publish to a private channel.
      const privateChannelName = `inbox_${peerId}`;
      await clientRef.current.publish(privateChannelName, messageData);
      return true;
    } catch (error) {
      console.error('[AgoraRTM] Failed to send private message:', error);
      return false;
    }
  }, [isLoggedIn, userId, userName]);

  // Subscribe to command topic (for PubSub compatibility)
  const subscribeToCommand = useCallback((topic, callback) => {
    const listeners = commandListenersRef.current;
    if (!listeners.has(topic)) {
      listeners.set(topic, new Set());
    }
    listeners.get(topic).add(callback);
    console.log(`[RTM] Subscribed to topic "${topic}". Total listeners: ${listeners.get(topic).size}`);

    // Return unsubscribe function
    return () => {
      listeners.get(topic)?.delete(callback);
      console.log(`[RTM] Unsubscribed from topic "${topic}". Remaining listeners: ${listeners.get(topic)?.size || 0}`);
    };
  }, []);

  // Channel metadata methods (replaces v1 channel attributes)
  // In Signaling v2, we use storage.setChannelMetadata / getChannelMetadata
  const setChannelAttribute = useCallback(async (key, value) => {
    if (!clientRef.current || !isLoggedIn || !isChannelReady || !channelNameStr) return false;
    try {
      const storage = clientRef.current.storage;
      // Signaling v2 API: setChannelMetadata(channelName, channelType, data: MetadataItem[], options?)
      const data = [{ key, value: JSON.stringify(value), revision: -1 }];
      await storage.setChannelMetadata(channelNameStr, 'MESSAGE', data, {});
      console.log(`[RTM] setChannelMetadata success for key "${key}"`);
      return true;
    } catch (error) {
      console.error(`[RTM] Failed to set channel metadata "${key}":`, error);
      return false;
    }
  }, [isLoggedIn, channelNameStr]);

  const getChannelAttributes = useCallback(async () => {
    if (!clientRef.current || !isLoggedIn || !isChannelReady || !channelNameStr) return {};
    try {
      const result = await clientRef.current.storage.getChannelMetadata(channelNameStr, 'MESSAGE');
      // Signaling v2 returns: { metadata: Record<string, { value, revision, updated }>, totalCount, majorRevision }
      const attrs = {};
      if (result?.metadata && typeof result.metadata === 'object') {
        Object.entries(result.metadata).forEach(([key, detail]) => {
          attrs[key] = { value: detail.value };
        });
      }
      return attrs;
    } catch (error) {
      // -10021 (STORAGE_METADATA_NOT_EXIST) is expected when no metadata has been set yet
      // -10025 (RTM not connected) happens during connection setup - safe to ignore
      const code = error?.code ?? error?.errorCode;
      if (code === -10021 || code === 'STORAGE_METADATA_NOT_EXIST' ||
          error?.message?.includes('NOT_EXIST') || error?.message?.includes('-10021') ||
          code === -10025 || error?.message?.includes('-10025') || error?.message?.includes('not connected')) {
        return {};
      }
      console.error('[RTM] Failed to get channel metadata:', error);
      return {};
    }
  }, [isLoggedIn, isChannelReady, channelNameStr]);

  const deleteChannelAttribute = useCallback(async (key) => {
    if (!clientRef.current || !isLoggedIn || !isChannelReady || !channelNameStr) return false;
    try {
      const storage = clientRef.current.storage;
      // Signaling v2 API: removeChannelMetadata(channelName, channelType, options?)
      // Pass data array in options to specify which keys to remove
      await storage.removeChannelMetadata(channelNameStr, 'MESSAGE', { data: [{ key }] });
      return true;
    } catch (error) {
      console.error(`[RTM] Failed to delete channel metadata "${key}":`, error);
      return false;
    }
  }, [isLoggedIn, channelNameStr]);

  // ==================== Phase 2: Presence API ====================

  // Set local user's presence state in channel
  const setPresenceState = useCallback(async (items) => {
    if (!clientRef.current || !isLoggedIn || !channelNameStr) return;
    try {
      await clientRef.current.presence.setState(channelNameStr, 'MESSAGE', items);
      console.log('[RTM] Presence state set:', items);
    } catch (error) {
      console.error('[RTM] Failed to set presence state:', error);
    }
  }, [isLoggedIn, channelNameStr]);

  // Remove specific keys from presence state
  const removePresenceState = useCallback(async (keys) => {
    if (!clientRef.current || !isLoggedIn || !channelNameStr) return;
    try {
      await clientRef.current.presence.removeState(channelNameStr, 'MESSAGE', keys);
      console.log('[RTM] Presence state removed:', keys);
    } catch (error) {
      console.error('[RTM] Failed to remove presence state:', error);
    }
  }, [isLoggedIn, channelNameStr]);

  // Get all online users with their state
  const getOnlineUsers = useCallback(async () => {
    if (!clientRef.current || !isLoggedIn || !channelNameStr) return { users: [], totalOccupancy: 0 };
    try {
      const result = await clientRef.current.presence.getOnlineUsers(channelNameStr, 'MESSAGE', {
        includedUserId: true,
        includedState: true,
      });
      return result;
    } catch (error) {
      console.error('[RTM] Failed to get online users:', error);
      return { users: [], totalOccupancy: 0 };
    }
  }, [isLoggedIn, channelNameStr]);

  // Get specific user's presence state
  const getUserPresenceState = useCallback(async (targetUserId) => {
    if (!clientRef.current || !isLoggedIn || !channelNameStr) return {};
    try {
      const result = await clientRef.current.presence.getState(channelNameStr, 'MESSAGE', targetUserId);
      return result?.state || result?.states || {};
    } catch (error) {
      console.error('[RTM] Failed to get user presence state:', error);
      return {};
    }
  }, [isLoggedIn, channelNameStr]);

  // Memoize typingUsers array to prevent unnecessary reference changes
  const typingUsersArray = useMemo(() => Array.from(typingUsers), [typingUsers]);

  const value = useMemo(() => ({
    rtmClient,
    isLoggedIn,
    isLoading,
    isChannelReady,
    messages,
    participants,
    typingUsers: typingUsersArray,
    sendMessage,
    sendCommand,
    sendTypingIndicator,
    sendPrivateMessage,
    subscribeToCommand,
    setChannelAttribute,
    getChannelAttributes,
    deleteChannelAttribute,
    channelName: channelNameStr,
    channelRef: { current: null }, // No longer a real channel ref in v2 (subscribe model)
    // Expose local user info for PubSub local emit
    localUserId: userId,
    localUserName: userName,
    // Flag to indicate this is a real provider context (not fallback)
    isRealContext: true,
    // Phase 2: Presence API
    setPresenceState,
    removePresenceState,
    getOnlineUsers,
    getUserPresenceState,
    presenceSnapshot,
  }), [
    rtmClient, isLoggedIn, isLoading, isChannelReady, messages, participants,
    typingUsersArray, sendMessage, sendCommand, sendTypingIndicator, sendPrivateMessage,
    subscribeToCommand, setChannelAttribute, getChannelAttributes, deleteChannelAttribute,
    channelNameStr, userId, userName,
    setPresenceState, removePresenceState, getOnlineUsers, getUserPresenceState, presenceSnapshot,
  ]);

  return (
    <AgoraRTMContext.Provider value={value}>
      {children}
    </AgoraRTMContext.Provider>
  );
}

export function useAgoraRTM() {
  const context = useContext(AgoraRTMContext);
  // Return null context if not within provider (allows optional usage)
  return context || {
    rtmClient: null,
    isLoggedIn: false,
    isLoading: false,
    isChannelReady: false,
    messages: [],
    participants: new Map(),
    typingUsers: [],
    sendMessage: async () => false,
    sendCommand: async () => false,
    sendTypingIndicator: async () => {},
    sendPrivateMessage: async () => false,
    subscribeToCommand: () => () => {},
    setChannelAttribute: async () => false,
    getChannelAttributes: async () => ({}),
    deleteChannelAttribute: async () => false,
    channelName: null,
    channelRef: { current: null },
    localUserId: null,
    localUserName: null,
    isRealContext: false,
    // Phase 2: Presence API fallbacks
    setPresenceState: async () => {},
    removePresenceState: async () => {},
    getOnlineUsers: async () => ({ users: [], totalOccupancy: 0 }),
    getUserPresenceState: async () => ({}),
    presenceSnapshot: new Map(),
  };
}

export default AgoraRTMContext;
